// La boutique « Investir en toi » : outils, santé et formations.
// Chaque achat coûte aujourd'hui et rapporte ensuite. Le matériel électronique et les chaussures de course
// s'usent : au bout de leur durée de vie, leur bonus s'arrête jusqu'à ce qu'on les remplace.
import type { TextKey } from './fr';
import type { GameState } from '../engine/state';

export type UpgradeCategory = 'outil' | 'sante' | 'formation';

export interface UpgradeEffect {
  /** Gain par heure de travail en extra (+10 % = 0,1). */
  pay?: number;
  energyMax?: number;
  /** Vitesse de retour de l'énergie. */
  regen?: number;
  /** Expérience gagnée. */
  xp?: number;
  /** Salaire (appliqué tout de suite, ou au premier emploi). */
  salary?: number;
  wellbeing?: number;
}

export interface UpgradeDef {
  id: string;
  category: UpgradeCategory;
  title: TextKey;
  desc: TextKey;
  price: number;
  effect: UpgradeEffect;
  /** Du matériel qui s'use : au bout de ces années, il ne donne plus de bonus jusqu'à son remplacement. */
  lifespan?: { years: number; worn: TextKey };
  /** Quand l'achat apparaît dans la boutique. */
  visible: (state: GameState) => boolean;
}

const afterSummer = (state: GameState) => state.energyUnlocked;

export const UPGRADES: UpgradeDef[] = [
  {
    // On l'a déjà à 18 ans (voir createInitialState) : il s'use en deux ans et demi.
    id: 'telephone',
    category: 'outil',
    title: 'upgrade.telephone.title',
    desc: 'upgrade.telephone.desc',
    price: 350,
    effect: { pay: 0.05 },
    lifespan: { years: 2.5, worn: 'upgrade.telephone.worn' },
    visible: () => true,
  },
  {
    id: 'sac',
    category: 'outil',
    title: 'upgrade.sac.title',
    desc: 'upgrade.sac.desc',
    price: 60,
    effect: { pay: 0.1 },
    visible: afterSummer,
  },
  {
    id: 'sommeil',
    category: 'sante',
    title: 'upgrade.sommeil.title',
    desc: 'upgrade.sommeil.desc',
    price: 50,
    effect: { regen: 0.25 },
    visible: afterSummer,
  },
  {
    id: 'course',
    category: 'sante',
    title: 'upgrade.course.title',
    desc: 'upgrade.course.desc',
    price: 80,
    effect: { energyMax: 5, wellbeing: 3 },
    // Une paire tient 500 à 800 km : six mois à un an, à trois sorties par semaine.
    lifespan: { years: 1, worn: 'upgrade.course.worn' },
    visible: afterSummer,
  },
  {
    id: 'anglais',
    category: 'formation',
    title: 'upgrade.anglais.title',
    desc: 'upgrade.anglais.desc',
    price: 300,
    effect: { xp: 0.15, salary: 0.02 },
    visible: afterSummer,
  },
  {
    id: 'trottinette',
    category: 'outil',
    title: 'upgrade.trottinette.title',
    desc: 'upgrade.trottinette.desc',
    price: 400,
    effect: { pay: 0.25 },
    lifespan: { years: 3, worn: 'upgrade.trottinette.worn' },
    visible: (state) => state.upgrades.includes('sac'),
  },
  {
    id: 'ordinateur',
    category: 'outil',
    title: 'upgrade.ordinateur.title',
    desc: 'upgrade.ordinateur.desc',
    price: 700,
    effect: { pay: 0.1, xp: 0.25 },
    lifespan: { years: 3, worn: 'upgrade.ordinateur.worn' },
    visible: afterSummer,
  },
  {
    id: 'permis',
    category: 'formation',
    title: 'upgrade.permis.title',
    desc: 'upgrade.permis.desc',
    price: 1200,
    effect: { salary: 0.03 },
    visible: afterSummer,
  },
  {
    id: 'certification',
    category: 'formation',
    title: 'upgrade.certification.title',
    desc: 'upgrade.certification.desc',
    price: 900,
    effect: { xp: 0.3, salary: 0.03 },
    visible: (state) => state.stage !== 'prologue',
  },
];
